import React, { type KeyboardEvent, useMemo, useRef, useState } from 'react';
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button as NewButton } from '@/components/ui/NewButton';
import { normalizeTaskState, type TaskState } from '@/components/tasks/constants';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import type { Task } from '@/components/ui/columns-tasks';
import { cn } from '@/lib/utils';
import { formatRegionalDate, weekStartsOnForLocale, type RegionalDateLocale } from '@/utils/date-utils';
import { toLocalDate } from '@/utils/temporal';

const EVENT_STYLES: Record<TaskState, { bg: string; text: string }> = {
  Extracted: { bg: 'bg-amber-50/70', text: 'text-amber-700' },
  Confirmed: { bg: 'bg-sky-50/70', text: 'text-sky-700' },
  Completed: { bg: 'bg-emerald-50/70', text: 'text-emerald-700' },
};

const OVERDUE_STYLE = { bg: 'bg-red-50/70', text: 'text-red-600' };
// Matches the table's deleted row (washed red, faded) and stays distinct from overdue.
const DELETED_STYLE = { bg: 'bg-red-50/40', text: 'text-gray-700' };

const CALENDAR_VIEWS = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
] as const;

type CalendarView = (typeof CALENDAR_VIEWS)[number]['value'];

function dateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

interface CalendarEvent {
  id: string;
  description: string;
  date: Date;
  state?: string;
  isDeleted?: boolean;
}

interface DeadlinesCalendarViewProps {
  /** All deadlines; those with a valid date are shown. */
  tasks: Task[];
  /** Open a deadline's detail when its event pill is clicked. */
  onSelectTask?: (taskId: string) => void;
}

function EventChip({
  event,
  day,
  today,
  onSelectTask,
  compact,
}: {
  event: CalendarEvent;
  day: Date;
  today: Date;
  onSelectTask?: (taskId: string) => void;
  compact?: boolean;
}) {
  const isDeleted = Boolean(event.isDeleted);
  const isOverdue = !isDeleted && day < today && event.state !== 'Completed';
  const style = isDeleted
    ? DELETED_STYLE
    : isOverdue
      ? OVERDUE_STYLE
      : EVENT_STYLES[normalizeTaskState(event.state)];
  const label = isDeleted ? `Deleted: ${event.description}` : event.description;
  return (
    <button
      type="button"
      onClick={() => onSelectTask?.(event.id)}
      title={label}
      aria-label={isDeleted ? label : undefined}
      className={cn(
        'w-full rounded px-1.5 text-left font-medium transition-colors hover:brightness-95',
        compact ? 'truncate py-1.5 text-[11px]' : 'whitespace-normal py-2 text-sm',
        isDeleted && 'opacity-70',
        style.bg,
        style.text,
      )}
    >
      {isDeleted && <span className="mr-1 font-semibold">Deleted</span>}
      <span className={cn(isDeleted && 'line-through')}>{event.description}</span>
    </button>
  );
}

function weekRangeLabel(anchorDate: Date, weekStartsOn: 0 | 1, locale: RegionalDateLocale): string {
  const weekStart = startOfWeek(anchorDate, { weekStartsOn });
  const weekEnd = endOfWeek(anchorDate, { weekStartsOn });
  const sameYear = weekStart.getFullYear() === weekEnd.getFullYear();
  return `${formatRegionalDate(weekStart, locale, { includeYear: !sameYear })} – ${formatRegionalDate(weekEnd, locale)}`;
}

function dayHeading(date: Date, locale: RegionalDateLocale): string {
  const weekday = format(date, 'EEEE');
  const datePart = formatRegionalDate(date, locale, { month: 'long' });
  return locale === 'us' ? `${weekday}, ${datePart}` : `${weekday} ${datePart}`;
}

export const DeadlinesCalendarView: React.FC<DeadlinesCalendarViewProps> = React.memo(({
  tasks,
  onSelectTask,
}) => {
  const locale = useMatterLocale();
  // UK weeks start Monday; US weeks start Sunday.
  const weekStartsOn = weekStartsOnForLocale(locale);

  const [view, setView] = useState<CalendarView>('month');
  const [anchorDate, setAnchorDate] = useState<Date>(() => startOfDay(new Date()));
  const viewButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedViewIndex = CALENDAR_VIEWS.findIndex((item) => item.value === view);

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    tasks.forEach((task) => {
      if (!task.date) return;
      const localDate = toLocalDate(task.date);
      if (Number.isNaN(localDate.getTime())) return;
      const key = dateKey(localDate);
      const list = map.get(key) ?? [];
      list.push({
        id: task.id,
        description: task.description,
        date: localDate,
        state: task.state,
        isDeleted: task.is_deleted,
      });
      map.set(key, list);
    });
    return map;
  }, [tasks]);

  const { weeks, weekdayLabels, weekDays } = useMemo(() => {
    const monthStart = startOfMonth(anchorDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn });
    const gridEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

    const grouped: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      grouped.push(days.slice(i, i + 7));
    }

    const currentWeekStart = startOfWeek(anchorDate, { weekStartsOn });
    const currentWeekEnd = endOfWeek(anchorDate, { weekStartsOn });
    const currentWeekDays = eachDayOfInterval({ start: currentWeekStart, end: currentWeekEnd });

    return {
      weeks: grouped,
      weekdayLabels: grouped[0]?.map((day) => format(day, 'EEE')) ?? [],
      weekDays: currentWeekDays,
    };
  }, [anchorDate, weekStartsOn]);

  const heading = useMemo(() => {
    if (view === 'month') return format(anchorDate, 'MMMM yyyy');
    if (view === 'week') return weekRangeLabel(anchorDate, weekStartsOn, locale);
    return dayHeading(anchorDate, locale);
  }, [anchorDate, locale, view, weekStartsOn]);

  const goToPrevious = () => {
    setAnchorDate((prev) => {
      if (view === 'week') return subWeeks(prev, 1);
      if (view === 'day') return subDays(prev, 1);
      return subMonths(prev, 1);
    });
  };

  const goToNext = () => {
    setAnchorDate((prev) => {
      if (view === 'week') return addWeeks(prev, 1);
      if (view === 'day') return addDays(prev, 1);
      return addMonths(prev, 1);
    });
  };

  const goToToday = () => setAnchorDate(startOfDay(new Date()));

  const openDayView = (day: Date) => {
    setAnchorDate(startOfDay(day));
    setView('day');
  };

  const handleViewKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % CALENDAR_VIEWS.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + CALENDAR_VIEWS.length) % CALENDAR_VIEWS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = CALENDAR_VIEWS.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    viewButtonRefs.current[nextIndex]?.focus();
    setView(CALENDAR_VIEWS[nextIndex].value);
  };

  const previousLabel = view === 'week' ? 'Previous week' : view === 'day' ? 'Previous day' : 'Previous month';
  const nextLabel = view === 'week' ? 'Next week' : view === 'day' ? 'Next day' : 'Next month';

  const renderDayCell = (day: Date, options: { inMonth?: boolean; showWeekday?: boolean; compact: boolean }) => {
    const key = dateKey(day);
    const dayEvents = eventsByDate.get(key) ?? [];
    const inMonth = options.inMonth ?? true;
    const isToday = isSameDay(day, today);

    return (
      <div
        key={key}
        className={cn(
          'flex min-h-0 flex-col overflow-hidden border-b border-r border-gray-200 p-1.5',
          !inMonth && 'bg-gray-50',
        )}
      >
        <div className="mb-1 flex items-center justify-between gap-1">
          <button
            type="button"
            onClick={() => openDayView(day)}
            aria-label={`Show ${dayHeading(day, locale)}`}
            className={cn(
              'flex items-center gap-1 rounded-full text-xs hover:bg-gray-100',
              options.showWeekday ? 'px-1.5 py-0.5' : 'h-6 w-6 justify-center',
              isToday
                ? 'bg-crimson-600 font-semibold text-white hover:bg-crimson-600'
                : inMonth
                  ? 'text-gray-700'
                  : 'text-gray-400',
            )}
          >
            {options.showWeekday && (
              <span className={cn('font-medium uppercase', isToday ? 'text-white/80' : 'text-gray-500')}>
                {format(day, 'EEE')}
              </span>
            )}
            <span>{day.getDate()}</span>
          </button>
          {dayEvents.length > 3 && (
            <span className="text-[10px] font-medium text-gray-400">
              {dayEvents.length}
            </span>
          )}
        </div>

        <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain">
          {dayEvents.map((event) => (
            <EventChip
              key={event.id}
              event={event}
              day={day}
              today={today}
              onSelectTask={onSelectTask}
              compact={options.compact}
            />
          ))}
        </div>
      </div>
    );
  };

  const dayEvents = eventsByDate.get(dateKey(anchorDate)) ?? [];

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-md border bg-white shadow-sm">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <h2 className="text-lg font-semibold text-gray-900">{heading}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="radiogroup"
            aria-label="Calendar view"
            className="inline-flex h-8 items-center rounded-md bg-slate-100 p-0.5"
          >
            {CALENDAR_VIEWS.map((item, index) => {
              const selected = view === item.value;
              return (
                <button
                  key={item.value}
                  ref={(el) => {
                    viewButtonRefs.current[index] = el;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  tabIndex={index === selectedViewIndex ? 0 : -1}
                  onClick={() => setView(item.value)}
                  onKeyDown={(event) => handleViewKeyDown(event, index)}
                  className={cn(
                    'h-full rounded-sm px-2.5 text-xs font-medium transition-colors',
                    selected
                      ? 'bg-white text-slate-900 shadow-xs'
                      : 'text-slate-500 hover:text-slate-700',
                  )}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          <NewButton variant="outline" size="sm" onClick={goToToday}>
            Today
          </NewButton>
          <NewButton
            variant="outline"
            size="sm"
            icon={ChevronLeft}
            onClick={goToPrevious}
            aria-label={previousLabel}
          />
          <NewButton
            variant="outline"
            size="sm"
            icon={ChevronRight}
            onClick={goToNext}
            aria-label={nextLabel}
          />
        </div>
      </div>

      {view === 'month' && (
        <>
          <div className="grid shrink-0 grid-cols-7 border-b border-gray-200">
            {weekdayLabels.map((label) => (
              <div
                key={label}
                className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wide text-gray-500"
              >
                {label}
              </div>
            ))}
          </div>
          <div
            className="grid min-h-0 flex-1 grid-cols-7 overflow-hidden"
            style={{ gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))` }}
          >
            {weeks.map((week) =>
              week.map((day) =>
                renderDayCell(day, {
                  inMonth: isSameMonth(day, anchorDate),
                  compact: true,
                }),
              ),
            )}
          </div>
        </>
      )}

      {view === 'week' && (
        <div
          className="grid min-h-0 flex-1 grid-cols-7 overflow-hidden"
          style={{ gridTemplateRows: 'minmax(0, 1fr)' }}
        >
          {weekDays.map((day) =>
            renderDayCell(day, {
              showWeekday: true,
              compact: false,
            }),
          )}
        </div>
      )}

      {view === 'day' && (
        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
          {dayEvents.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No deadlines on this day.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {dayEvents.map((event) => (
                <EventChip
                  key={event.id}
                  event={event}
                  day={anchorDate}
                  today={today}
                  onSelectTask={onSelectTask}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

DeadlinesCalendarView.displayName = 'DeadlinesCalendarView';
